import { defineStore } from "pinia"
import { buildApiUrl } from "@/utils/url"
import { ILoginResponse, ICurrentUser } from "@/models/user"
import { popupBasicSuccess } from "@/utils/notification"
import { handleApiError, ensureApiStatusCode } from "@/utils/error_handling"
import axios, { AxiosError } from "axios"
import Swal from "sweetalert2"
import { h } from "vue"

const CURRENT_USER = "user"

function clearCurrentUserLocalStorage() {
    localStorage.removeItem(CURRENT_USER)
}

function loadCurrentUserLocalStorage(): ICurrentUser | null {
    const rawObj: string | null = localStorage.getItem(CURRENT_USER)
    if (rawObj == null) {
        return null
    }
    const parsedObj: ICurrentUser = JSON.parse(rawObj)

    const currentUser: ICurrentUser = {
        user_id: parsedObj.user_id,
        user_name: parsedObj.user_name,
        access: parsedObj.access,
        refresh: parsedObj.refresh,
        token_lifetime: parsedObj.token_lifetime,
        created: parsedObj.created,
        token_expire: parsedObj.token_expire,
    }

    return currentUser
}

function saveCurrentUserLocalStorage(currentUser: ICurrentUser) {
    try {
        const cleanUser = {
            user_id: currentUser?.user_id,
            user_name: currentUser?.user_name,
            access: currentUser?.access,
            refresh: currentUser?.refresh,
            token_lifetime: currentUser?.token_lifetime,
            created: currentUser?.created,
            token_expire: currentUser?.token_expire,
        }
        localStorage.setItem(CURRENT_USER, JSON.stringify(cleanUser))
    } catch (e) {
        console.warn("Failed to serialize currentUser to localStorage", e)
    }
}

export const useAuthStore = defineStore({
    id: "auth",
    state: () => ({
        currentUser: loadCurrentUserLocalStorage(),
        loading: false as boolean,
        error: null as null | string,
    }),
    getters: {
        isAuthenticated: (state): boolean => {
            if (state.currentUser) {
                return true
            }
            return false
        },
        currentUserValue: (state): ICurrentUser | null => {
            return state.currentUser
        },
    },
    actions: {
        async login(email: string, password: string): Promise<boolean> {
            const url = buildApiUrl("/auth/jwt/create/")
            try {
                const response = await axios.post(url, {
                    email: email,
                    password: password,
                })
                ensureApiStatusCode(response, 200)
                const data: ILoginResponse = response.data

                const timestamp_seconds = Math.round(new Date().getTime() / 1000)

                const currentUser: ICurrentUser = {
                    user_id: data.user_id,
                    user_name: data.user_name,
                    access: data.access,
                    refresh: data.refresh,
                    token_lifetime: data.token_lifetime,
                    created: timestamp_seconds,
                    token_expire: timestamp_seconds + data.token_lifetime,
                }

                saveCurrentUserLocalStorage(currentUser)
                this.currentUser = loadCurrentUserLocalStorage()
                return true
            } catch (error) {
                this.error = handleApiError(error)
            }
            return false
        },
        saveRegisterToCurrentUser(data: any) {
            const timestamp_seconds = Math.round(new Date().getTime() / 1000)

            const currentUser: ICurrentUser = {
                user_id: 1,
                user_name: data.user_name,
                access: data.access,
                refresh: data.refresh,
                token_lifetime: 87300000,
                created: timestamp_seconds,
                token_expire: timestamp_seconds + data.token_lifetime,
            }

            saveCurrentUserLocalStorage(currentUser)

            this.currentUser = loadCurrentUserLocalStorage()
        },
        async checkAuth() {
            if (!this.currentUser) {
                return
            }
            const url = buildApiUrl("/auth/jwt/verify/")
            await fetch(url, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    token: this.currentUser.access,
                }),
            }).then((response) => {
                if (response.status === 200) {
                    // Update token
                } else {
                    clearCurrentUserLocalStorage()
                    this.currentUser = null
                }
            })
        },
        async logout(options?: { redirect?: boolean; redirectTo?: string }) {
            clearCurrentUserLocalStorage()
            this.currentUser = null
            const shouldRedirect = options?.redirect ?? true
            if (shouldRedirect) {
                this.router.push(options?.redirectTo ?? "/")
            }
        },
        getCurrentUser(): ICurrentUser | null {
            return this.currentUser
        },

        async verify_phone(force: boolean, phone_number: string) {
            this.loading = true
            const url = buildApiUrl("/users/phone/verify/")
            if (!this.currentUser) {
                return false
            } else {
                try {
                    const response = await axios.post(
                        url,
                        {
                            phone_number: phone_number,
                            force_new_code: force,
                        },
                        {
                            headers: {
                                Authorization: `JWT ${this.currentUser.access}`,
                            },
                        }
                    )

                    ensureApiStatusCode(response, 201) // Raises exception when status is not 201
                    if (force) popupBasicSuccess("A new verification code has been sent to your phone.")
                    return true
                } catch (error: unknown) {
                    this.error = handleApiError(error)
                    return false
                } finally {
                    this.loading = false
                }
            }
        },
        async activate_phone(code: string, phone_number: string) {
            this.loading = true
            const url = buildApiUrl("/users/phone/activate/")
            if (!this.currentUser) {
                return false
            } else {
                try {
                    const response = await axios.post(
                        url,
                        {
                            phone_number: phone_number,
                            code: code,
                        },
                        {
                            headers: {
                                Authorization: `JWT ${this.currentUser.access}`,
                            },
                        }
                    )
                    const resultCode = response.status
                    ensureApiStatusCode(response, 201)
                    popupBasicSuccess("Your phone number has been verified.")
                    return resultCode == 201
                } catch (error: unknown) {
                    this.error = handleApiError(error)
                    return false
                } finally {
                    this.loading = false
                }
            }
        },

        async upgrade_account(upgrade_to: string) {
            if (!this.currentUser) {
                return false
            } else {
                const url = buildApiUrl("/users/upgrade/")
                try {
                    const response = await axios.post(
                        url,
                        {
                            upgrade_to: upgrade_to,
                        },
                        {
                            headers: {
                                Authorization: `JWT ${this.currentUser.access}`,
                            },
                        }
                    )
                    const resultCode = response.status
                    ensureApiStatusCode(response, 201)
                    popupBasicSuccess("Your account has been upgraded to " + upgrade_to + ".")
                    return resultCode == 201
                } catch (error: unknown) {
                    this.error = handleApiError(error)
                    return false
                } finally {
                    this.loading = false
                }
            }
        },
        async me() {
            if (!this.currentUser) {
                return false
            } else {
                try {
                    const token = this.currentUser.access
                    const url = buildApiUrl("/users/me/")
                    const response = await fetch(url, {
                        headers: { Authorization: `JWT ${token}` },
                    })

                    return await response.json()
                } catch (error) {
                    console.log(error)
                    return false
                }
            }
        },
        async pick_name(first_name_slug: string, last_name_slug: string): Promise<boolean> {
            if (!this.currentUser) {
                return false
            }
            this.loading = true
            const url = buildApiUrl("/users/name_generator/pick/")
            try {
                const token = this.currentUser.access
                const response = await axios.post(
                    url,
                    {
                        first_name_slug: first_name_slug,
                        last_name_slug: last_name_slug,
                    },
                    {
                        headers: {
                            Authorization: `JWT ${token}`,
                        },
                    }
                )
                ensureApiStatusCode(response, 201)

                popupBasicSuccess("Your screen name has been saved.")

                return true
            } catch (error: unknown) {
                this.error = handleApiError(error)
            } finally {
                this.loading = false
            }
            return false
        },
        async follow(username: string) {
            if (!this.currentUser || !username) {
                return false
            } else {
                try {
                    const token = this.currentUser.access
                    const url = buildApiUrl(`/users/${username}/follow/`)

                    const response = await axios.post(url, null, {
                        headers: { Authorization: `JWT ${token}` },
                    })

                    popupBasicSuccess(`Success follow ${username}`)

                    return await response.data
                } catch (error) {
                    this.error = handleApiError(error)
                    return false
                }
            }
        },
        async unfollow(username: string) {
            if (!this.currentUser || !username) {
                return false
            } else {
                try {
                    const token = this.currentUser.access
                    const url = buildApiUrl(`/users/${username}/unfollow/`)

                    const response = await axios.post(url, null, {
                        headers: { Authorization: `JWT ${token}` },
                    })

                    popupBasicSuccess(`Success unfollow ${username}`)

                    return await response.data
                } catch (error) {
                    this.error = handleApiError(error)
                    return false
                }
            }
        },
        async following(username: string) {
            if (!this.currentUser || !username) {
                return false
            } else {
                try {
                    const token = this.currentUser.access
                    const url = buildApiUrl(`/users/${username}/following/`)
                    const response = await fetch(url, {
                        headers: { Authorization: `JWT ${token}` },
                    })

                    return await response.json()
                } catch (error) {
                    console.log(error)
                    return false
                }
            }
        },
        async followers(username: string) {
            if (!this.currentUser || !username) {
                return false
            } else {
                try {
                    const token = this.currentUser.access
                    const url = buildApiUrl(`/users/${username}/followers/`)
                    const response = await fetch(url, {
                        headers: { Authorization: `JWT ${token}` },
                    })

                    return await response.json()
                } catch (error) {
                    console.log(error)
                    return false
                }
            }
        },
        async getUsers(sort: string, limit: number = 10) {
            try {
                const url = buildApiUrl(`/users/`)
                const response = await axios.get(url, {
                    params: {
                        sort: sort,
                        limit: limit,
                    },
                })
                return response.data.results
            } catch (error) {
                console.log(error)
                return false
            }
        },
        async user(username: string) {
            if (!username) {
                return false
            }

            try {
                const url = buildApiUrl(`/users/${username}/`)
                const response = await fetch(url)
                return await response.json()
            } catch (error) {
                console.log(error)
                return false
            }
        },
        async userInfo(username: string) {
            if (!username) {
                return false
            }

            try {
                const url = buildApiUrl(`/users/${username}/personal_information/`)
                const response = await fetch(url)
                return await response.json()
            } catch (error) {
                console.log(error)
                return false
            }
        },
        async options() {
            try {
                const url = buildApiUrl(`/users/settings/options/`)
                const response = await fetch(url)
                return await response.json()
            } catch (error) {
                console.log(error)
                return false
            }
        },

        async favoritesCategories() {
            if (!this.currentUser) {
                return false
            } else {
                try {
                    const token = this.currentUser.access
                    const url = buildApiUrl(`/categories/favorites/`)
                    const response = await fetch(url, {
                        headers: { Authorization: `JWT ${token}` },
                    })

                    return await response.json()
                } catch (error) {
                    console.log(error)
                    return false
                }
            }
        },

        async favoriteCategory(slug: string) {
            if (!this.currentUser) {
                return false
            } else {
                try {
                    const token = this.currentUser.access
                    const url = buildApiUrl(`/categories/${slug}/favorite/`)
                    const response = await axios.post(url, null, {
                        headers: { Authorization: `JWT ${token}` },
                    })

                    ensureApiStatusCode(response, 201)
                    popupBasicSuccess(`Success add category to your favorites`)
                    return await response.data
                } catch (error) {
                    this.error = handleApiError(error)
                    return false
                }
            }
        },

        async unfavoriteCategory(slug: string) {
            if (!this.currentUser) {
                return false
            } else {
                try {
                    const token = this.currentUser.access
                    const url = buildApiUrl(`/categories/${slug}/unfavorite/`)
                    const response = await axios.post(url, null, {
                        headers: { Authorization: `JWT ${token}` },
                    })
                    ensureApiStatusCode(response, 204)
                    popupBasicSuccess(`Success remove category from your favorites`)
                    return await response.data
                } catch (error) {
                    this.error = handleApiError(error)
                    return false
                }
            }
        },

        async savePersonalInformation(data: any) {
            if (!this.currentUser) {
                return false
            } else {
                try {
                    const token = this.currentUser.access
                    const url = buildApiUrl(`/users/personal_information/save/`)
                    const response = await axios.post(url, data, {
                        headers: { Authorization: `JWT ${token}`, "Content-Type": "application/json" },
                    })
                    ensureApiStatusCode(response, 200) // Raises exception when status is not 201
                    popupBasicSuccess("Success save personal information")
                    return response.data
                } catch (error) {
                    this.error = handleApiError(error)
                    return false
                }
            }
        },

        async getPersonalInformation() {
            if (!this.currentUser) {
                return false
            } else {
                try {
                    const token = this.currentUser.access
                    const url = buildApiUrl(`/users/personal_information/`)
                    const response = await axios.get(url, {
                        headers: { Authorization: `JWT ${token}`, "Content-Type": "application/json" },
                    })
                    return response.data
                } catch (error) {
                    this.error = handleApiError(error)
                    return false
                }
            }
        },

        async getVisibilityFriendship() {
            if (!this.currentUser) {
                return false
            } else {
                try {
                    const token = this.currentUser.access
                    const url = buildApiUrl(`/friends/settings/`)
                    const response = await axios.get(url, {
                        headers: { Authorization: `JWT ${token}`, "Content-Type": "application/json" },
                    })
                    return response.data
                } catch (error) {
                    this.error = handleApiError(error)
                    return false
                }
            }
        },

        async saveVisibilityFriendship(data: any) {
            if (!this.currentUser) {
                return false
            } else {
                try {
                    const token = this.currentUser.access
                    const url = buildApiUrl(`/friends/settings/save/`)
                    const response = await axios.post(
                        url,
                        { friendship_visibility: data },
                        {
                            headers: { Authorization: `JWT ${token}`, "Content-Type": "application/json" },
                        }
                    )
                    ensureApiStatusCode(response, 200) // Raises exception when status is not 201
                    popupBasicSuccess("Success save visibility friendship")
                    return response.data
                } catch (error) {
                    this.error = handleApiError(error)
                    return false
                }
            }
        },
    },
})
